import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, ShieldCheck, LogOut, X } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import Icon from '../components/AppIcon';
import { getOpenSupportThreadsCount, subscribeToSupportUpdates } from '../lib/adminApi';

const navigationItems = [
  { label: 'Overview', path: '/admin/dashboard', icon: 'LayoutDashboard' },
  { label: 'Orders', path: '/admin/orders', icon: 'ClipboardList' },
  { label: 'Payments', path: '/admin/payments', icon: 'CreditCard' },
  { label: 'Escrows', path: '/admin/escrows', icon: 'Lock' },
  { label: 'Bids', path: '/admin/bids', icon: 'FileText' },
  { label: 'Payouts', path: '/admin/payouts', icon: 'Wallet' },
  { label: 'Events', path: '/admin/events', icon: 'Calendar' },
  { label: 'Caterers', path: '/admin/caterers', icon: 'Store' },
  { label: 'Caterer Claims', path: '/admin/caterer-claims', icon: 'UserCheck' },
  { label: 'Customers', path: '/admin/customers', icon: 'Users' },
  { label: 'Compliance', path: '/admin/compliance', icon: 'ShieldCheck' },
  { label: 'System Monitoring', path: '/admin/platform', icon: 'Activity' },
  { label: 'Analytics & AI', path: '/admin/analytics', icon: 'Sparkles' },
  { label: 'Observability', path: '/admin/observability', icon: 'List' },
  { label: 'Escalation Centre', path: '/admin/escalations', icon: 'AlertTriangle' },
  { label: 'Support', path: '/admin/support', icon: 'Headphones' },
  { label: 'Notify Users', path: '/admin/notify-users', icon: 'Send' },
  { label: 'Settings', path: '/admin/settings', icon: 'Settings' },
];

function AdminSidebar({ isMobile = false, onNavigate, openSupportCount = 0 }) {
  const location = useLocation();

  return (
    <aside
      className={[
        'w-64 shrink-0 border-r border-border bg-card/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/85',
        isMobile ? 'fixed inset-y-0 z-40 translate-x-0 lg:hidden' : 'hidden lg:block',
      ].join(' ')}
    >
      {isMobile && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3 text-left">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mrs Cater</p>
              <p className="text-sm font-semibold text-foreground">Admin Console</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onNavigate}
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      <div className="flex items-center gap-3 px-6 py-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Mrs Cater</p>
          <p className="text-base font-bold text-foreground">Admin Console</p>
        </div>
      </div>

      <nav className="space-y-1 px-3 pb-6">
        {navigationItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onNavigate ? () => onNavigate() : undefined}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              ].join(' ')
            }
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-background text-foreground shadow-sm">
              <Icon name={item.icon} size={18} />
            </span>
            <span>{item.label}</span>
            {item.path === '/admin/support' && openSupportCount > 0 && (
              <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-destructive-foreground">
                {openSupportCount > 99 ? '99+' : openSupportCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

export default function AdminLayout() {
  const { profile, signOut } = useAdminAuth();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openSupportCount, setOpenSupportCount] = useState(0);

  useEffect(() => {
    getOpenSupportThreadsCount().then(setOpenSupportCount).catch(() => setOpenSupportCount(0));
    const unsubscribe = subscribeToSupportUpdates(() => {
      getOpenSupportThreadsCount().then(setOpenSupportCount).catch(() => setOpenSupportCount(0));
    });
    return unsubscribe;
  }, []);

  const activeItem = useMemo(
    () => navigationItems.find((item) => location.pathname.startsWith(item.path)) ?? navigationItems[0],
    [location.pathname]
  );

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar openSupportCount={openSupportCount} />
      {mobileNavOpen && (
        <>
          <div className="fixed inset-0 z-30 bg-black/25 backdrop-blur-sm lg:hidden" onClick={() => setMobileNavOpen(false)} />
          <AdminSidebar isMobile onNavigate={() => setMobileNavOpen(false)} openSupportCount={openSupportCount} />
        </>
      )}

      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card/80 px-6 py-4 backdrop-blur supports-[backdrop-filter]:bg-card/70">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted lg:hidden"
              aria-label="Open navigation"
              onClick={() => setMobileNavOpen((prev) => !prev)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Admin</p>
              <h1 className="text-lg font-semibold text-foreground">{activeItem.label}</h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right md:block">
              <p className="text-sm font-semibold text-foreground">{profile?.full_name ?? 'Admin'}</p>
              <p className="text-xs text-muted-foreground">{profile?.email ?? 'admin@mrscater.com'}</p>
            </div>
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-10">
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
